import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Toolbar,
  AppBar,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Snackbar,
  AlertColor,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import FilterListIcon from '@mui/icons-material/FilterList';
import api from '../services/api';
import { getUserData } from '../utils/cookies';
import { PageName, ApiResponse, User } from '../types';
import {
  DrawerNavigation,
  DRAWER_WIDTH,
  COLLAPSED_WIDTH,
  ConfirmDialog,
} from '../components';

// ---- Types ----

interface SuperSerieListItem {
  id: string;
  label: string;
  series: { id: string }[];
}

interface SeriesListResponse {
  makes: { [makeId: string]: { label: string } };
  series: { [serieId: string]: { label: string; make: string } };
  models: { [modelId: string]: { label: string; serie: string } };
}

interface SerieInfo {
  id: string;
  label: string;
  makeId: string;
  makeLabel: string;
  displayLabel: string; // "Make - Serie"
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

type RightPanelMode = 'none' | 'superSerie' | 'orphans';

interface SuperSeriesEditorProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function SuperSeriesEditor({ onLogout, onNavigate }: SuperSeriesEditorProps) {
  // UI States
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data states
  const [superSeries, setSuperSeries] = useState<SuperSerieListItem[]>([]);
  const [serieInfoMap, setSerieInfoMap] = useState<Map<string, SerieInfo>>(new Map());

  // Selection states
  const [selectedSuperSerieId, setSelectedSuperSerieId] = useState<string | null>(null);
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('none');
  const [selectedSerieIds, setSelectedSerieIds] = useState<string[]>([]);

  // Search states
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [showEmptyOnly, setShowEmptyOnly] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  // Rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameLabel, setRenameLabel] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [superSerieToDelete, setSuperSerieToDelete] = useState<SuperSerieListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSelectedIds, setAssignSelectedIds] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);

  // Assign orphan to existing super-serie dialog
  const [assignOrphanDialogOpen, setAssignOrphanDialogOpen] = useState(false);
  const [orphanToAssign, setOrphanToAssign] = useState<SerieInfo | null>(null);
  const [assignOrphanTargetId, setAssignOrphanTargetId] = useState('');
  const [assignOrphanSearch, setAssignOrphanSearch] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const hasFetched = useRef(false);

  // Load user data
  useEffect(() => {
    const userData = getUserData();
    if (userData) {
      setUser({
        id: userData.id,
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        roles: userData.roles || [],
      });
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [superSeriesRes, seriesRes] = await Promise.all([
        api.get<ApiResponse<SuperSerieListItem[]>>('super-series/list'),
        api.get<ApiResponse<SeriesListResponse>>('series/list'),
      ]);

      const ssData = superSeriesRes.data.results;
      setSuperSeries(ssData);

      const sData = seriesRes.data.results;
      const infoMap = new Map<string, SerieInfo>();
      for (const [serieId, serie] of Object.entries(sData.series)) {
        const make = sData.makes[serie.make];
        infoMap.set(serieId, {
          id: serieId,
          label: serie.label,
          makeId: serie.make,
          makeLabel: make?.label ?? 'Unknown',
          displayLabel: `${make?.label ?? 'Unknown'} - ${serie.label}`,
        });
      }
      setSerieInfoMap(infoMap);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, [fetchData]);

  const refreshData = useCallback(async () => {
    hasFetched.current = false;
    await fetchData();
    hasFetched.current = true;
  }, [fetchData]);

  // ---- Derived data ----

  // Set of all assigned serie IDs across all super-series
  const assignedSerieIds = useMemo(() => {
    const set = new Set<string>();
    for (const ss of superSeries) {
      for (const s of ss.series) {
        set.add(s.id);
      }
    }
    return set;
  }, [superSeries]);

  // Orphan series: in serieInfoMap but not assigned to any super-serie
  const orphanSeries = useMemo(() => {
    const orphans: SerieInfo[] = [];
    for (const [id, info] of serieInfoMap) {
      if (!assignedSerieIds.has(id)) {
        orphans.push(info);
      }
    }
    return orphans.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [serieInfoMap, assignedSerieIds]);

  // Super-series with valid count and stale detection
  const enrichedSuperSeries = useMemo(() => {
    return superSeries.map((ss) => {
      const validSeries = ss.series.filter((s) => serieInfoMap.has(s.id));
      const staleCount = ss.series.length - validSeries.length;
      return {
        ...ss,
        validCount: validSeries.length,
        staleCount,
      };
    });
  }, [superSeries, serieInfoMap]);

  // Filtered left column
  const filteredSuperSeries = useMemo(() => {
    const search = leftSearch.toLowerCase();
    let list = search
      ? enrichedSuperSeries.filter((ss) => ss.label.toLowerCase().includes(search))
      : enrichedSuperSeries;
    if (showEmptyOnly) {
      list = list.filter((ss) => ss.validCount === 0);
    }
    return [...list].sort((a, b) => a.label.localeCompare(b.label));
  }, [enrichedSuperSeries, leftSearch, showEmptyOnly]);

  // Empty super-series in current filtered list
  const emptyFilteredSuperSeries = useMemo(() =>
    filteredSuperSeries.filter((ss) => ss.validCount === 0),
    [filteredSuperSeries]
  );

  // Currently selected super-serie (enriched)
  const selectedSuperSerie = useMemo(() => {
    if (!selectedSuperSerieId) return null;
    return enrichedSuperSeries.find((ss) => ss.id === selectedSuperSerieId) ?? null;
  }, [enrichedSuperSeries, selectedSuperSerieId]);

  // Assigned series for selected super-serie
  const assignedSeriesForSelected = useMemo(() => {
    if (!selectedSuperSerie) return [];
    return selectedSuperSerie.series
      .map((s) => serieInfoMap.get(s.id))
      .filter((info): info is SerieInfo => info !== undefined)
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [selectedSuperSerie, serieInfoMap]);

  // Filtered right column (assigned series)
  const filteredAssignedSeries = useMemo(() => {
    const search = rightSearch.toLowerCase();
    if (!search) return assignedSeriesForSelected;
    return assignedSeriesForSelected.filter((s) => s.displayLabel.toLowerCase().includes(search));
  }, [assignedSeriesForSelected, rightSearch]);

  // Filtered orphan series
  const filteredOrphans = useMemo(() => {
    const search = rightSearch.toLowerCase();
    if (!search) return orphanSeries;
    return orphanSeries.filter((s) => s.displayLabel.toLowerCase().includes(search));
  }, [orphanSeries, rightSearch]);

  // Map: serieId -> superSerieId (for showing "in: X" in assign dialog)
  const serieToSuperSerieMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const ss of superSeries) {
      for (const s of ss.series) {
        map.set(s.id, ss.id);
      }
    }
    return map;
  }, [superSeries]);

  // Super-serie label map for quick lookups
  const superSerieLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const ss of superSeries) {
      map.set(ss.id, ss.label);
    }
    return map;
  }, [superSeries]);

  // Series available for assignment (not in the current super-serie)
  const assignableSeriesForDialog = useMemo(() => {
    if (!selectedSuperSerieId) return [];
    const currentSerieIds = new Set(
      superSeries.find((ss) => ss.id === selectedSuperSerieId)?.series.map((s) => s.id) ?? []
    );
    const search = assignSearch.toLowerCase();
    return Array.from(serieInfoMap.values())
      .filter((info) => !currentSerieIds.has(info.id))
      .filter((info) => !search || info.displayLabel.toLowerCase().includes(search))
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [serieInfoMap, superSeries, selectedSuperSerieId, assignSearch]);

  // Filtered super-series for orphan assign dialog
  const filteredSuperSeriesForOrphanAssign = useMemo(() => {
    const search = assignOrphanSearch.toLowerCase();
    const list = search
      ? enrichedSuperSeries.filter((ss) => ss.label.toLowerCase().includes(search))
      : enrichedSuperSeries;
    return [...list].sort((a, b) => a.label.localeCompare(b.label));
  }, [enrichedSuperSeries, assignOrphanSearch]);

  // ---- Handlers ----

  const showSnackbar = (message: string, severity: AlertColor) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSelectSuperSerie = (id: string) => {
    setSelectedSuperSerieId(id);
    setRightPanelMode('superSerie');
    setSelectedSerieIds([]);
    setRightSearch('');
  };

  const handleViewOrphans = () => {
    setSelectedSuperSerieId(null);
    setRightPanelMode('orphans');
    setSelectedSerieIds([]);
    setRightSearch('');
  };

  // Create super-serie
  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    try {
      setCreating(true);
      await api.post('super-series/create', { label: newLabel.trim() });
      showSnackbar(`Super serie "${newLabel}" created`, 'success');
      setCreateDialogOpen(false);
      setNewLabel('');
      await refreshData();
    } catch (err) {
      console.error('Failed to create super serie:', err);
      showSnackbar('Failed to create super serie', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Rename super-serie
  const handleStartRename = (ss: SuperSerieListItem) => {
    setRenamingId(ss.id);
    setRenameLabel(ss.label);
  };

  const handleRename = async () => {
    if (!renamingId || !renameLabel.trim()) return;
    try {
      setRenaming(true);
      await api.post(`super-series/edit/${renamingId}`, { label: renameLabel.trim() });
      showSnackbar('Super serie renamed', 'success');
      setRenamingId(null);
      await refreshData();
    } catch (err) {
      console.error('Failed to rename super serie:', err);
      showSnackbar('Failed to rename super serie', 'error');
    } finally {
      setRenaming(false);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setRenamingId(null);
    }
  };

  // Delete super-serie
  const handleDeleteClick = (ss: SuperSerieListItem) => {
    setSuperSerieToDelete(ss);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!superSerieToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`super-series/delete/${superSerieToDelete.id}`);
      showSnackbar(`Super serie "${superSerieToDelete.label}" deleted`, 'success');
      setDeleteDialogOpen(false);
      setSuperSerieToDelete(null);
      if (selectedSuperSerieId === superSerieToDelete.id) {
        setSelectedSuperSerieId(null);
        setRightPanelMode('none');
      }
      await refreshData();
    } catch (err) {
      console.error('Failed to delete super serie:', err);
      showSnackbar('Failed to delete super serie. It may have series attached.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete empty super-series
  const handleBulkDeleteEmpty = async () => {
    const toDelete = emptyFilteredSuperSeries;
    if (toDelete.length === 0) return;
    try {
      setBulkDeleting(true);
      const results = await Promise.allSettled(
        toDelete.map((ss) => api.delete(`super-series/delete/${ss.id}`))
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        showSnackbar(`Deleted ${succeeded} empty super series`, 'success');
      } else {
        showSnackbar(`Deleted ${succeeded}, failed ${failed}`, 'warning');
      }
      setBulkDeleteDialogOpen(false);
      const deletedIds = new Set(toDelete.map((ss) => ss.id));
      if (selectedSuperSerieId && deletedIds.has(selectedSuperSerieId)) {
        setSelectedSuperSerieId(null);
        setRightPanelMode('none');
      }
      await refreshData();
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      showSnackbar('Failed to delete super series', 'error');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clean up stale references
  const handleCleanup = async () => {
    if (!selectedSuperSerie) return;
    const validIds = selectedSuperSerie.series
      .filter((s) => serieInfoMap.has(s.id))
      .map((s) => s.id);
    try {
      await api.post(`super-series/assign-series/${selectedSuperSerie.id}`, { series: validIds });
      showSnackbar(`Removed ${selectedSuperSerie.staleCount} stale reference(s)`, 'success');
      await refreshData();
    } catch (err) {
      console.error('Failed to clean up stale references:', err);
      showSnackbar('Failed to clean up stale references', 'error');
    }
  };

  // Toggle serie selection in right panel
  const handleToggleSerieSelection = (serieId: string) => {
    setSelectedSerieIds((prev) =>
      prev.includes(serieId) ? prev.filter((id) => id !== serieId) : [...prev, serieId]
    );
  };

  // Remove selected series from super-serie
  const handleRemoveSeries = async () => {
    if (!selectedSuperSerieId || selectedSerieIds.length === 0) return;
    const current = superSeries.find((ss) => ss.id === selectedSuperSerieId);
    if (!current) return;

    const remaining = current.series
      .map((s) => s.id)
      .filter((id) => !selectedSerieIds.includes(id));

    try {
      await api.post(`super-series/assign-series/${selectedSuperSerieId}`, { series: remaining });
      showSnackbar(`Removed ${selectedSerieIds.length} serie(s) from super serie`, 'success');
      setSelectedSerieIds([]);
      await refreshData();
    } catch (err) {
      console.error('Failed to remove series:', err);
      showSnackbar('Failed to remove series', 'error');
    }
  };

  // Assign series from dialog
  const handleAssignSeries = async () => {
    if (!selectedSuperSerieId || assignSelectedIds.length === 0) return;
    try {
      setAssigning(true);

      // Remove assigned series from their old super-series
      const reassignments = new Map<string, string[]>(); // oldSsId -> remaining serieIds
      for (const serieId of assignSelectedIds) {
        const oldSsId = serieToSuperSerieMap.get(serieId);
        if (oldSsId && oldSsId !== selectedSuperSerieId) {
          if (!reassignments.has(oldSsId)) {
            const oldSs = superSeries.find((ss) => ss.id === oldSsId);
            if (oldSs) {
              reassignments.set(
                oldSsId,
                oldSs.series.map((s) => s.id).filter((id) => !assignSelectedIds.includes(id))
              );
            }
          }
        }
      }

      // Update old super-series first
      for (const [ssId, remaining] of reassignments) {
        await api.post(`super-series/assign-series/${ssId}`, { series: remaining });
      }

      // Add to current super-serie
      const currentSs = superSeries.find((ss) => ss.id === selectedSuperSerieId);
      const newList = [...(currentSs?.series.map((s) => s.id) ?? []), ...assignSelectedIds];
      await api.post(`super-series/assign-series/${selectedSuperSerieId}`, { series: newList });

      showSnackbar(`Assigned ${assignSelectedIds.length} serie(s)`, 'success');
      setAssignDialogOpen(false);
      setAssignSelectedIds([]);
      setAssignSearch('');
      await refreshData();
    } catch (err) {
      console.error('Failed to assign series:', err);
      showSnackbar('Failed to assign series', 'error');
    } finally {
      setAssigning(false);
    }
  };

  // Create super-serie for orphan
  const handleCreateForOrphan = async (serie: SerieInfo) => {
    const label = `${serie.makeLabel} ${serie.label}`;
    try {
      const res = await api.post<ApiResponse<{ id: string }>>('super-series/create', { label });
      const newId = res.data.results.id;
      await api.post(`super-series/assign-series/${newId}`, { series: [serie.id] });
      showSnackbar(`Created super serie "${label}" and assigned serie`, 'success');
      await refreshData();
    } catch (err) {
      console.error('Failed to create super serie for orphan:', err);
      showSnackbar('Failed to create super serie', 'error');
    }
  };

  // Assign orphan to existing super-serie
  const handleAssignOrphan = async () => {
    if (!orphanToAssign || !assignOrphanTargetId) return;
    try {
      const targetSs = superSeries.find((ss) => ss.id === assignOrphanTargetId);
      if (!targetSs) return;
      const newList = [...targetSs.series.map((s) => s.id), orphanToAssign.id];
      await api.post(`super-series/assign-series/${assignOrphanTargetId}`, { series: newList });
      showSnackbar(`Assigned "${orphanToAssign.displayLabel}" to super serie`, 'success');
      setAssignOrphanDialogOpen(false);
      setOrphanToAssign(null);
      setAssignOrphanTargetId('');
      setAssignOrphanSearch('');
      await refreshData();
    } catch (err) {
      console.error('Failed to assign orphan:', err);
      showSnackbar('Failed to assign serie', 'error');
    }
  };

  const handleOpenAssignOrphanDialog = (serie: SerieInfo) => {
    setOrphanToAssign(serie);
    setAssignOrphanTargetId('');
    setAssignOrphanSearch('');
    setAssignOrphanDialogOpen(true);
  };

  const handleRefresh = () => {
    refreshData();
  };

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // ---- Render ----

  return (
    <Box sx={{ display: 'flex' }}>
      <DrawerNavigation
        currentPage="superSeriesEditor"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { xs: 0, md: `${currentDrawerWidth}px` },
          transition: 'margin-left 0.2s, width 0.2s',
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
            ml: { xs: 0, md: `${currentDrawerWidth}px` },
            transition: 'margin-left 0.2s, width 0.2s',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Super Series Editor
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {user.firstName} {user.lastName}
              </Typography>
            )}
            <IconButton color="inherit" onClick={handleRefresh} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton color="inherit" onClick={onLogout}>
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Toolbar />

        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 150px)' }}>
              {/* Left Column - Super Series List */}
              <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Super Series ({enrichedSuperSeries.length})
                  </Typography>
                  {emptyFilteredSuperSeries.length > 0 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setBulkDeleteDialogOpen(true)}
                    >
                      Delete empty ({emptyFilteredSuperSeries.length})
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Create
                  </Button>
                </Box>

                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search super series..."
                    value={leftSearch}
                    onChange={(e) => setLeftSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title={showEmptyOnly ? 'Show all' : 'Show only empty'}>
                    <IconButton
                      size="small"
                      onClick={() => setShowEmptyOnly(!showEmptyOnly)}
                      color={showEmptyOnly ? 'warning' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Orphan warning */}
                {orphanSeries.length > 0 && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Alert
                      severity="warning"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleViewOrphans}
                        >
                          View
                        </Button>
                      }
                      sx={{ py: 0 }}
                    >
                      {orphanSeries.length} orphan serie(s)
                    </Alert>
                  </Box>
                )}

                <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                  {filteredSuperSeries.map((ss) => (
                    <ListItem
                      key={ss.id}
                      disablePadding
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {ss.staleCount > 0 && (
                            <Tooltip title={`${ss.staleCount} stale reference(s)`}>
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartRename(ss);
                            }}
                            title="Rename"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {ss.validCount === 0 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(ss);
                              }}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      }
                    >
                      <ListItemButton
                        selected={selectedSuperSerieId === ss.id && rightPanelMode === 'superSerie'}
                        onClick={() => handleSelectSuperSerie(ss.id)}
                      >
                        {renamingId === ss.id ? (
                          <TextField
                            size="small"
                            value={renameLabel}
                            onChange={(e) => setRenameLabel(e.target.value)}
                            onKeyDown={handleRenameKeyDown}
                            onBlur={handleRename}
                            autoFocus
                            disabled={renaming}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mr: 1 }}
                          />
                        ) : (
                          <ListItemText primary={ss.label} />
                        )}
                        <Chip
                          label={ss.validCount}
                          size="small"
                          sx={{ ml: 1, mr: 6 }}
                          color={ss.validCount === 0 ? 'default' : 'primary'}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {filteredSuperSeries.length === 0 && (
                    <ListItem>
                      <ListItemText
                        secondary={leftSearch ? 'No matches found' : 'No super series'}
                        sx={{ textAlign: 'center' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* Right Column */}
              <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {rightPanelMode === 'none' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">
                      Select a super serie to view its series
                    </Typography>
                  </Box>
                )}

                {rightPanelMode === 'superSerie' && selectedSuperSerie && (
                  <>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {selectedSuperSerie.label} ({assignedSeriesForSelected.length})
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setAssignDialogOpen(true);
                          setAssignSelectedIds([]);
                          setAssignSearch('');
                        }}
                      >
                        Assign Series
                      </Button>
                    </Box>

                    {/* Stale references warning */}
                    {selectedSuperSerie.staleCount > 0 && (
                      <Box sx={{ px: 2, pt: 1 }}>
                        <Alert
                          severity="warning"
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              startIcon={<CleaningServicesIcon />}
                              onClick={handleCleanup}
                            >
                              Clean up
                            </Button>
                          }
                          sx={{ py: 0 }}
                        >
                          {selectedSuperSerie.staleCount} stale reference(s) to non-existent series
                        </Alert>
                      </Box>
                    )}

                    <Box sx={{ px: 2, py: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search assigned series..."
                        value={rightSearch}
                        onChange={(e) => setRightSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Remove action bar */}
                    {selectedSerieIds.length > 0 && (
                      <Box sx={{ px: 2, pb: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          color="warning"
                          onClick={handleRemoveSeries}
                        >
                          Remove {selectedSerieIds.length} serie(s) from super serie
                        </Button>
                      </Box>
                    )}

                    <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                      {filteredAssignedSeries.map((serie) => (
                        <ListItem key={serie.id} disablePadding>
                          <ListItemButton onClick={() => handleToggleSerieSelection(serie.id)}>
                            <Checkbox
                              edge="start"
                              checked={selectedSerieIds.includes(serie.id)}
                              tabIndex={-1}
                              disableRipple
                            />
                            <ListItemText primary={serie.displayLabel} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                      {filteredAssignedSeries.length === 0 && (
                        <ListItem>
                          <ListItemText
                            secondary={rightSearch ? 'No matches found' : 'No series assigned'}
                            sx={{ textAlign: 'center' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {rightPanelMode === 'orphans' && (
                  <>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6">
                        Orphan Series ({orphanSeries.length})
                      </Typography>
                    </Box>

                    <Box sx={{ px: 2, py: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search orphan series..."
                        value={rightSearch}
                        onChange={(e) => setRightSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                      {filteredOrphans.map((serie) => (
                        <ListItem
                          key={serie.id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCreateForOrphan(serie)}
                              >
                                Create SS
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenAssignOrphanDialog(serie)}
                              >
                                Assign to...
                              </Button>
                            </Box>
                          }
                        >
                          <ListItemText primary={serie.displayLabel} />
                        </ListItem>
                      ))}
                      {filteredOrphans.length === 0 && (
                        <ListItem>
                          <ListItemText
                            secondary={rightSearch ? 'No matches found' : 'No orphan series'}
                            sx={{ textAlign: 'center' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </Paper>
            </Box>
          )}
        </Container>
      </Box>

      {/* Create Super Serie Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Super Serie</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Label"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newLabel.trim()) handleCreate();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !newLabel.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Super Serie"
        message={`Are you sure you want to delete "${superSerieToDelete?.label}"?`}
        confirmLabel="Delete"
        loading={deleting}
      />

      {/* Bulk Delete Empty Confirmation */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDeleteEmpty}
        title="Delete Empty Super Series"
        message={`Delete ${emptyFilteredSuperSeries.length} empty super series? This cannot be undone.`}
        confirmLabel="Delete All"
        loading={bulkDeleting}
      />

      {/* Assign Series Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Series</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            placeholder="Search series..."
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            sx={{ mb: 1, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {assignableSeriesForDialog.map((serie) => {
              const existingSsId = serieToSuperSerieMap.get(serie.id);
              const existingSsLabel = existingSsId ? superSerieLabelMap.get(existingSsId) : null;
              return (
                <ListItem key={serie.id} disablePadding>
                  <ListItemButton
                    onClick={() =>
                      setAssignSelectedIds((prev) =>
                        prev.includes(serie.id)
                          ? prev.filter((id) => id !== serie.id)
                          : [...prev, serie.id]
                      )
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={assignSelectedIds.includes(serie.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={serie.displayLabel}
                      secondary={existingSsLabel ? `in: ${existingSsLabel}` : undefined}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {assignableSeriesForDialog.length === 0 && (
              <ListItem>
                <ListItemText
                  secondary="No available series"
                  sx={{ textAlign: 'center' }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignSeries}
            variant="contained"
            disabled={assigning || assignSelectedIds.length === 0}
          >
            {assigning ? <CircularProgress size={24} /> : `Assign (${assignSelectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Orphan to Existing Super Serie Dialog */}
      <Dialog
        open={assignOrphanDialogOpen}
        onClose={() => setAssignOrphanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign "{orphanToAssign?.displayLabel}" to Super Serie
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            placeholder="Search super series..."
            value={assignOrphanSearch}
            onChange={(e) => setAssignOrphanSearch(e.target.value)}
            sx={{ mb: 1, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredSuperSeriesForOrphanAssign.map((ss) => (
              <ListItem key={ss.id} disablePadding>
                <ListItemButton
                  selected={assignOrphanTargetId === ss.id}
                  onClick={() => setAssignOrphanTargetId(ss.id)}
                >
                  <ListItemText primary={ss.label} />
                  <Chip label={ss.validCount} size="small" sx={{ ml: 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
            {filteredSuperSeriesForOrphanAssign.length === 0 && (
              <ListItem>
                <ListItemText
                  secondary="No super series found"
                  sx={{ textAlign: 'center' }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOrphanDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignOrphan}
            variant="contained"
            disabled={!assignOrphanTargetId}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SuperSeriesEditor;
